/**
 * The "you died, here is the way back" prompt — PRACTICE ROOMS ONLY.
 *
 * A wagered death is final and gets `DeathCam`: killer view, free camera, live
 * pot, requeue. This is the other thing entirely — a gym or scrim death, where
 * coming back is the whole point and the only question is how long you wait.
 *
 * THE BUTTON IS THE RESPAWN; THE TIMER IS THE FALLBACK. It would have been
 * easier to run a countdown and let the player skip it, but a cooldown you can
 * skip is not a cooldown — it is a delay that only taxes whoever is paying
 * attention. So the button is live immediately, and the countdown exists for
 * someone who has stepped away from the keyboard. Practice is for reps, and
 * dead time teaches nothing.
 */

const AUTO_MS = 5000;

export class RespawnPrompt {
  constructor({ onRespawn } = {}) {
    this.onRespawn = onRespawn ?? (() => {});
    this.root = null;
    this._deadline = 0;
    this._raf = 0;
  }

  /** Dead in a room that brings you back. Idempotent — a second death while
   *  already showing just restarts the clock. */
  show() {
    this._deadline = performance.now() + AUTO_MS;
    if (this.root) return;

    // Built node by node rather than with innerHTML. Nothing here is dynamic
    // today, which is exactly why the template would survive until someone
    // interpolated a player name into it — and a killer's display name is
    // attacker-controlled text.
    const el = document.createElement('div');
    el.className = 'respawn-prompt';

    const title = document.createElement('p');
    title.className = 'respawn-title';
    title.textContent = 'ELIMINATED';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'respawn-btn';
    btn.textContent = 'Respawn';
    // `onRespawn` and not a direct connection call: the gym revives locally and
    // the scrim asks the server, and this component should not know which.
    btn.addEventListener('click', () => this.onRespawn());

    const sub = document.createElement('p');
    sub.className = 'respawn-sub';
    const count = document.createElement('span');
    count.textContent = '5';
    sub.append('back in ', count, 's');

    el.append(title, btn, sub);
    (document.getElementById('announce-layer') ?? document.body).appendChild(el);
    this.root = el;
    this._count = count;
    this._tick();
  }

  /** Alive again — by the button, the timer, god mode or a round roll. */
  hide() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
    this.root?.remove();
    this.root = null;
  }

  _tick = () => {
    if (!this.root) return;
    const left = Math.max(0, this._deadline - performance.now());
    if (this._count) this._count.textContent = String(Math.ceil(left / 1000));
    // The prompt does NOT respawn anyone when it reaches zero. Whoever owns the
    // auto-respawn already does that — the gym on a tick count, the scrim on
    // the server — and a second trigger here would race them.
    this._raf = requestAnimationFrame(this._tick);
  };
}
